// src/components/ProfilePage.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useUser, API_BASE } from "../context/UserContext";
import { subscribePush, unsubscribePush, isPushSubscribed } from "../utils/pushNotifications";
import CuratedLists from "./CuratedLists";
import BestPickerModal from "./BestPickerModal";
import { STATUS_LABEL, STATUS_COLOR, STATUS_EMOJI, FRONTEND_URL, BEST_CATEGORIES } from "../constants";
import { shareProfileCard } from "../utils/generateShareCard";
import { useTranslation } from "react-i18next";
import { LANGUAGES, changeLanguage } from "../i18n";

const FH = "'Noto Serif', Georgia, serif";
const FL = "'Manrope', -apple-system, sans-serif";
const C = {
  primary:          "#655d54",
  primaryDim:       "#595149",
  primaryContainer: "#ede0d5",
  bg:               "#faf9f6",
  surfaceLow:       "#f4f4f0",
  surfaceLowest:    "#ffffff",
  container:        "#edeeea",
  onSurface:        "#2f3430",
  onSurfaceVariant: "#5c605c",
  outlineVariant:   "#8a8e8a",
  error:            "#9e422c",
};

const isMobile = () => window.innerWidth <= 768;

// -- Input field -------------------------------------------------------
function ProfileInput({ label, style: extraStyle = {}, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label style={{
        display: "block", fontFamily: FL,
        fontSize: 12, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.15em",
        color: C.outlineVariant, marginBottom: 6,
      }}>
        {label}
      </label>
      <input
        {...props}
        onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
        onBlur={(e) => { setFocused(false); props.onBlur?.(e); }}
        style={{
          width: "100%", padding: "10px 12px",
          background: focused ? C.container : C.surfaceLow,
          border: "none", borderRadius: 8,
          fontFamily: FL, fontSize: 16, color: C.onSurface,
          outline: "none", boxSizing: "border-box",
          WebkitAppearance: "none",
          transition: "background 0.15s",
          ...extraStyle,
        }}
      />
    </div>
  );
}

// -- Toggle switch -----------------------------------------------------
function Toggle({ value, onChange }) {
  return (
    <div
      onClick={onChange}
      style={{
        width: 44, height: 24, borderRadius: 12,
        background: value ? C.primary : C.container,
        position: "relative", cursor: "pointer", flexShrink: 0,
        transition: "background 0.2s",
      }}
    >
      <div style={{
        position: "absolute", top: 3, left: value ? 22 : 3,
        width: 18, height: 18, borderRadius: "50%",
        background: value ? "#fff6ef" : C.outlineVariant,
        boxShadow: "0 1px 4px rgba(47,52,48,0.15)",
        transition: "left 0.2s, background 0.2s",
      }} />
    </div>
  );
}

// -- Section card (tonal surface, no border) ---------------------------
function Card({ children, style = {}, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: C.surfaceLowest,
      borderRadius: 10, padding: "14px 16px",
      marginBottom: 10,
      boxShadow: "0 1px 8px rgba(47,52,48,0.05)",
      ...style,
    }}>
      {children}
    </div>
  );
}

// -- Mini map (inline) -------------------------------------------------
function MiniMapInline({ places, onViewMap }) {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  useEffect(() => {
    if (!mapRef.current || places.length === 0) return;
    const check = setInterval(() => {
      if (window.google?.maps) {
        clearInterval(check);
        const bounds = new window.google.maps.LatLngBounds();
        places.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
        mapInstance.current = new window.google.maps.Map(mapRef.current, {
          center: bounds.getCenter(), zoom: 12,
          mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
          zoomControl: false,
          gestureHandling: "none",
          mapId: process.env.REACT_APP_GOOGLE_MAP_ID || "DEMO_MAP_ID",
        });
        if (places.length > 1) mapInstance.current.fitBounds(bounds, 30);
        else mapInstance.current.setZoom(14);
        const STATUS_DOT = { want_to_go: "#BA7517", visited: "#1D9E75", want_revisit: "#D4537E" };
        places.forEach((p) => {
          const color = STATUS_DOT[p.status] || "#655d54";
          const content = document.createElement("div");
          content.innerHTML = `<div style="width:8px;height:8px;border-radius:50%;background:${color};border:1.5px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.2);"></div>`;
          new window.google.maps.marker.AdvancedMarkerElement({
            position: { lat: p.lat, lng: p.lng },
            map: mapInstance.current,
            content,
          });
        });
      }
    }, 100);
    return () => { clearInterval(check); mapInstance.current = null; };
  }, [places]);

  const STATUS_EMOJI = { want_to_go: "\u{1F516}", visited: "\u2705", want_revisit: "\u2764\uFE0F" };
  const counts = {};
  places.forEach((p) => { counts[p.status] = (counts[p.status] || 0) + 1; });

  if (places.length === 0) return null;

  return (
    <div style={{ marginTop: 12 }}>
      <div ref={mapRef} style={{
        width: "100%", aspectRatio: "4/3", borderRadius: 10,
        overflow: "hidden", background: C.surfaceLow, marginBottom: 8,
      }} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        {Object.entries(counts).map(([status, count]) => (
          <span key={status} style={{
            fontFamily: FL, fontSize: 14, color: C.onSurfaceVariant,
            display: "flex", alignItems: "center", gap: 4,
          }}>
            {STATUS_EMOJI[status] || "\u{1F4CD}"} {count}
          </span>
        ))}
        <button onClick={onViewMap} style={{
          marginLeft: "auto", padding: "4px 10px",
          border: "1px solid rgba(101,93,84,0.15)", borderRadius: 6,
          background: "none", fontFamily: FL, fontSize: 14, fontWeight: 600,
          color: C.primary, cursor: "pointer",
        }}>
          {t("profile.view_on_map")}
        </button>
      </div>
    </div>
  );
}

function MiniMap({ places, onViewMap }) {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  useEffect(() => {
    if (!mapRef.current || places.length === 0) return;
    const check = setInterval(() => {
      if (window.google?.maps) {
        clearInterval(check);
        const bounds = new window.google.maps.LatLngBounds();
        places.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));

        mapInstance.current = new window.google.maps.Map(mapRef.current, {
          center: bounds.getCenter(),
          zoom: 12,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          gestureHandling: "none",
          mapId: process.env.REACT_APP_GOOGLE_MAP_ID || "DEMO_MAP_ID",
        });

        if (places.length > 1) {
          mapInstance.current.fitBounds(bounds, 30);
        } else {
          mapInstance.current.setZoom(14);
        }

        const STATUS_DOT = {
          want_to_go: "#BA7517", visited: "#1D9E75",
          want_revisit: "#D4537E",
        };

        places.forEach((p) => {
          const color = STATUS_DOT[p.status] || "#655d54";
          const content = document.createElement("div");
          content.innerHTML = `<div style="width:8px;height:8px;border-radius:50%;background:${color};border:1.5px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.2);"></div>`;
          new window.google.maps.marker.AdvancedMarkerElement({
            position: { lat: p.lat, lng: p.lng },
            map: mapInstance.current,
            content,
          });
        });
      }
    }, 100);
    return () => {
      clearInterval(check);
      mapInstance.current = null;
    };
  }, [places]);

  const STATUS_EMOJI = { want_to_go: "\u{1F516}", visited: "\u2705", want_revisit: "\u2764\uFE0F" };
  const counts = {};
  places.forEach((p) => { counts[p.status] = (counts[p.status] || 0) + 1; });

  return (
    <Card>
      <p style={{
        margin: "0 0 12px", fontFamily: FL, fontSize: 12, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.15em", color: C.outlineVariant,
      }}>
        {t("profile.my_space_map")}
      </p>

      {places.length > 0 ? (
        <>
          <div
            id="profile-minimap"
            ref={mapRef}
            style={{
              width: "100%", aspectRatio: "4/5", borderRadius: 10,
              overflow: "hidden", background: C.surfaceLow, marginBottom: 12,
            }}
          />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            {Object.entries(counts).map(([status, count]) => (
              <span key={status} style={{
                fontFamily: FL, fontSize: 14, color: C.onSurfaceVariant,
                display: "flex", alignItems: "center", gap: 4,
              }}>
                {STATUS_EMOJI[status] || "\u{1F4CD}"} {count}
              </span>
            ))}
            <span style={{ fontFamily: FL, fontSize: 14, color: C.outlineVariant }}>
              {places.length} total
            </span>
          </div>
        </>
      ) : (
        <p style={{ fontFamily: FH, fontStyle: "italic", fontSize: 16, color: C.outlineVariant, margin: "0 0 12px" }}>
          {t("profile.no_places")}
        </p>
      )}

      <button
        onClick={onViewMap}
        style={{
          width: "100%", padding: "10px",
          border: "1px solid rgba(101,93,84,0.15)",
          borderRadius: 8, background: "none",
          fontFamily: FL, fontSize: 14, fontWeight: 700,
          textTransform: "uppercase", letterSpacing: "0.08em",
          color: C.primary, cursor: "pointer",
          transition: "background 0.15s",
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = C.primaryContainer}
        onMouseLeave={(e) => e.currentTarget.style.background = "none"}
      >
        {t("profile.view_on_map")}
      </button>
    </Card>
  );
}


// -- Place feed card ---------------------------------------------------
function PlaceFeedCard({ place: p, mobile }) {
  const photos = p.photo_urls?.length ? p.photo_urls : (p.photo_url ? [p.photo_url] : []);
  const [imgIdx, setImgIdx] = useState(0);
  const sc = STATUS_COLOR[p.status];

  return (
    <div style={{
      background: C.surfaceLowest, borderRadius: mobile ? 0 : 12,
      overflow: "hidden",
      boxShadow: mobile ? "none" : "0 1px 6px rgba(47,52,48,0.05)",
    }}>
      {photos.length > 0 ? (
        <div style={{ position: "relative" }}>
          <div style={{ width: "100%", overflow: "hidden", aspectRatio: "5/4", background: "#f0efec" }}>
            <img src={photos[imgIdx]} alt={p.name}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          </div>
          {photos.length > 1 && (
            <>
              {imgIdx > 0 && (
                <button onClick={() => setImgIdx((i) => i - 1)} style={{
                  position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)",
                  width: 26, height: 26, borderRadius: "50%",
                  background: "rgba(0,0,0,0.3)", border: "none", color: "white",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 15 }}>chevron_left</span>
                </button>
              )}
              {imgIdx < photos.length - 1 && (
                <button onClick={() => setImgIdx((i) => i + 1)} style={{
                  position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                  width: 26, height: 26, borderRadius: "50%",
                  background: "rgba(0,0,0,0.3)", border: "none", color: "white",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 15 }}>chevron_right</span>
                </button>
              )}
              <div style={{
                position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)",
                display: "flex", gap: 4,
              }}>
                {photos.map((_, i) => (
                  <div key={i} style={{
                    width: 5, height: 5, borderRadius: "50%",
                    background: i === imgIdx ? "white" : "rgba(255,255,255,0.4)",
                  }} />
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <div style={{
          padding: "28px 16px",
          background: `linear-gradient(135deg, ${C.primaryDim}15, ${C.primary}08)`,
        }}>
          <p style={{ margin: 0, fontFamily: FH, fontStyle: "italic", fontSize: 18, color: C.primary, textAlign: "center" }}>
            {p.name}
          </p>
        </div>
      )}
      <div style={{ padding: "10px 14px 14px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: p.memo ? 6 : 0 }}>
          <span style={{
            fontFamily: FL, fontSize: 14, fontWeight: 600,
            padding: "2px 7px", borderRadius: 4,
            background: sc?.bg || C.surfaceLow, color: sc?.color || C.onSurfaceVariant,
          }}>
            {STATUS_EMOJI[p.status]} {STATUS_LABEL[p.status]}
          </span>
          {p.rating > 0 && (
            <span style={{
              fontFamily: FL, fontSize: 14, padding: "2px 7px",
              background: C.primaryContainer, color: C.primary,
              borderRadius: 4, fontWeight: 600,
            }}>
              {"\u2605".repeat(p.rating)}{"\u2606".repeat(5 - p.rating)}
            </span>
          )}
          {photos.length > 0 && (
            <span style={{ fontFamily: FH, fontSize: 16, fontWeight: 600, color: C.onSurface }}>
              {p.name}
            </span>
          )}
        </div>
        {p.memo && (
          <p style={{
            margin: "4px 0 0", fontFamily: FH, fontStyle: "italic",
            fontSize: 16, color: C.onSurfaceVariant, lineHeight: 1.6,
          }}>
            "{p.memo}"
          </p>
        )}
        {p.address && (
          <p style={{
            margin: "4px 0 0", fontFamily: FL, fontSize: 14, color: C.outlineVariant,
            display: "flex", alignItems: "center", gap: 3,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>location_on</span>
            {p.address}
          </p>
        )}
      </div>
    </div>
  );
}

// -- My Best section ---------------------------------------------------
function MyBestSection({ myBestPicks, personalPlaces, onBestPickAdd, onBestPickRemove }) {
  const [pickerCat, setPickerCat] = useState(null);

  return (
    <>
      {BEST_CATEGORIES.map(({ key, emoji, label }) => {
        const picks = myBestPicks[key] || [];
        return (
          <div key={key} style={{ marginBottom: 16 }}>
            <p style={{
              margin: "0 0 8px", fontFamily: FL, fontSize: 14, fontWeight: 600,
              color: C.onSurfaceVariant,
            }}>
              {emoji} {label}
              <span style={{ fontWeight: 400, color: C.outlineVariant, marginLeft: 6, fontSize: 12 }}>
                {picks.length}/5
              </span>
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {/* Filled slots */}
              {picks.map((p) => (
                <div key={p.id} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 10px", borderRadius: 8, background: C.surfaceLow,
                }}>
                  <span style={{
                    flex: 1, fontFamily: FL, fontSize: 16, fontWeight: 600, color: C.onSurface,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {p.name}
                  </span>
                  {p.address && (
                    <span style={{
                      fontFamily: FL, fontSize: 14, color: C.outlineVariant,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      maxWidth: 120, flexShrink: 1,
                    }}>
                      {p.address}
                    </span>
                  )}
                  <button
                    onClick={() => onBestPickRemove(p.id)}
                    style={{
                      width: 22, height: 22, padding: 0, border: "none",
                      background: "none", cursor: "pointer", flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 14, color: C.outlineVariant }}>close</span>
                  </button>
                </div>
              ))}

              {/* Empty slot */}
              {picks.length < 5 && (
                <button
                  onClick={() => setPickerCat({ key, label: `${emoji} ${label}` })}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "9px 10px", borderRadius: 8,
                    border: `1.5px dashed ${C.container}`, background: "none",
                    cursor: "pointer",
                    fontFamily: FL, fontSize: 14, fontStyle: "italic",
                    color: C.outlineVariant,
                    transition: "border-color 0.15s, background 0.15s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.background = C.surfaceLow; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.container; e.currentTarget.style.background = "none"; }}
                >
                  + Add a pick
                </button>
              )}
            </div>
          </div>
        );
      })}

      {pickerCat && (
        <BestPickerModal
          category={pickerCat.key}
          categoryLabel={pickerCat.label}
          personalPlaces={personalPlaces}
          myBestPicks={myBestPicks}
          onSelect={(data) => { onBestPickAdd(data); setPickerCat(null); }}
          onClose={() => setPickerCat(null)}
        />
      )}
    </>
  );
}

// -- Collapsible section header ----------------------------------------
function SectionHeader({ title, open, onToggle, extra }) {
  return (
    <div
      onClick={onToggle}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        cursor: "pointer", marginBottom: open ? 14 : 0,
      }}
    >
      <p style={{
        margin: 0, fontFamily: FL, fontSize: 12, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.15em", color: C.outlineVariant,
      }}>
        {title}
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {extra}
        <span className="material-symbols-outlined" style={{
          fontSize: 16, color: C.outlineVariant,
          transition: "transform 0.2s",
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
        }}>
          expand_more
        </span>
      </div>
    </div>
  );
}

export default function ProfilePage({ personalPlaces = [], onViewMap, onPlaceClick, onViewUserProfile, myBestPicks = {}, onBestPickAdd, onBestPickRemove, onRefresh, pushNav, onStartTutorial }) {
  const { user, updateUser, logout } = useUser();
  const mobile = isMobile();
  const [showMyPlaces, setShowMyPlaces] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [myFolders, setMyFolders] = useState([]);
  const [openSections, setOpenSections] = useState({ curation: false, best: false, follow: false, pin: false });
  const toggleSection = (key) => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  const { t, i18n } = useTranslation();

  const handleRefresh = async () => {
    setRefreshing(true);
    await onRefresh?.();
    fetchFollowLists();
    setTimeout(() => setRefreshing(false), 600);
  };
  const photoInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [editing, setEditing]       = useState(false);
  const [nickname, setNickname]     = useState(user?.nickname || "");
  const [instagramUrl, setInstagramUrl] = useState(user?.instagram_url || "");
  const [isPublic, setIsPublic]     = useState(user?.is_public || false);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState("");
  const [nickStatus, setNickStatus] = useState(null); // null | "checking" | "available" | "taken"
  const nickCheckTimer = useRef(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [showPinChange, setShowPinChange] = useState(false);
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin]         = useState("");
  const [pinError, setPinError]     = useState("");
  const [pinSaving, setPinSaving]   = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const pushSupported = "PushManager" in window;
  const [googleError, setGoogleError] = useState("");

  // -- Load folders --
  useEffect(() => {
    if (!user) return;
    axios.get(`${API_BASE}/folders/`)
      .then((res) => setMyFolders(res.data)).catch(() => {});
  }, [user]);

  // -- Follow management state --
  const [followingList, setFollowingList] = useState([]);
  const [followerList, setFollowerList]   = useState([]);
  const [followSearch, setFollowSearch]   = useState("");
  const [followSearchResults, setFollowSearchResults] = useState([]);
  const [followSearching, setFollowSearching] = useState(false); // eslint-disable-line no-unused-vars
  const [followActionLoading, setFollowActionLoading] = useState(null);

  useEffect(() => {
    isPushSubscribed().then(setPushEnabled);
  }, []);

  const [pushError, setPushError] = useState("");

  const handlePushToggle = async () => {
    setPushLoading(true);
    setPushError("");
    try {
      if (pushEnabled) {
        await unsubscribePush();
        setPushEnabled(false);
      } else {
        const ok = await subscribePush();
        if (!ok) {
          setPushError("Notification permission denied or server configuration required");
        }
        setPushEnabled(ok);
      }
    } catch (e) {
      console.error("Push toggle error:", e);
      setPushError(e.message || "An error occurred while updating notification settings");
    }
    setPushLoading(false);
  };

  // -- Fetch follow lists --
  const fetchFollowLists = useCallback(() => {
    if (!user) return;
    axios.get(`${API_BASE}/follows/me/following`)
      .then((res) => setFollowingList(res.data)).catch(() => {});
    axios.get(`${API_BASE}/follows/me/followers`)
      .then((res) => setFollowerList(res.data)).catch(() => {});
  }, [user]);

  useEffect(() => { fetchFollowLists(); }, [fetchFollowLists]);

  const handleFollowSearch = async () => { // eslint-disable-line no-unused-vars
    if (!followSearch.trim()) return;
    setFollowSearching(true);
    try {
      const res = await axios.get(`${API_BASE}/users/search?q=${encodeURIComponent(followSearch.trim())}`);
      const results = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
      setFollowSearchResults(results.filter((u) => u.id !== user.user_id));
    } catch {
      setFollowSearchResults([]);
    } finally { setFollowSearching(false); }
  };

  const handleFollow = async (targetId) => { // eslint-disable-line no-unused-vars
    setFollowActionLoading(targetId);
    try {
      await axios.post(`${API_BASE}/follows/${targetId}`);
      fetchFollowLists();
    } catch {} finally { setFollowActionLoading(null); }
  };

  const handleUnfollow = async (targetId) => {
    setFollowActionLoading(targetId);
    try {
      await axios.delete(`${API_BASE}/follows/${targetId}`);
      fetchFollowLists();
    } catch {} finally { setFollowActionLoading(null); }
  };

  const checkNickname = useCallback((nick) => {
    clearTimeout(nickCheckTimer.current);
    if (nick.trim() === user?.nickname) { setNickStatus(null); return; }
    if (nick.trim().length < 2) { setNickStatus(null); return; }
    setNickStatus("checking");
    nickCheckTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_BASE}/users/check-nickname?nickname=${encodeURIComponent(nick.trim())}&exclude_user_id=${user.user_id}`);
        setNickStatus(res.data.available ? "available" : "taken");
      } catch { setNickStatus(null); }
    }, 400);
  }, [user]);

  if (!user) return null;

  const handleSave = async () => {
    setError("");
    if (nickname.trim().length < 2) { setError(t("auth.nickname_min")); return; }
    if (nickStatus === "taken") { setError(t("auth.nickname_taken")); return; }
    setSaving(true);
    try {
      const res = await axios.patch(`${API_BASE}/users/me`, {
        nickname: nickname.trim(),
        instagram_url: instagramUrl.trim() || null,
        is_public: isPublic,
      });
      updateUser({
        nickname: res.data.nickname,
        instagram_url: res.data.instagram_url,
        is_public: res.data.is_public,
      });
      setEditing(false);
      setSuccessMsg(t("profile.saved"));
      setTimeout(() => setSuccessMsg(""), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const handlePublicToggle = async () => {
    const newVal = !isPublic;
    setIsPublic(newVal);
    try {
      await axios.patch(`${API_BASE}/users/me`, { is_public: newVal });
      updateUser({ is_public: newVal });
    } catch (e) { setIsPublic(!newVal); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const uploadRes = await axios.post(`${API_BASE}/upload/photo`, form);
      const url = uploadRes.data.url;
      await axios.patch(`${API_BASE}/users/me`, { profile_photo_url: url });
      updateUser({ profile_photo_url: url });
      setSuccessMsg("Profile photo updated!");
      setTimeout(() => setSuccessMsg(""), 2500);
    } catch (err) {
      setError(t("profile.photo_failed"));
    } finally { setUploadingPhoto(false); }
  };

  const handlePinChange = async () => {
    setPinError("");
    if (user.has_pin) {
      if (!/^\d{6}$/.test(currentPin)) { setPinError("Please enter your current 6-digit PIN"); return; }
      if (currentPin === newPin) { setPinError("New PIN is the same as the current one"); return; }
    }
    if (!/^\d{6}$/.test(newPin)) { setPinError("Please enter a new 6-digit PIN"); return; }
    setPinSaving(true);
    try {
      if (user.has_pin) {
        await axios.post(`${API_BASE}/users/login`, { nickname: user.nickname, pin: currentPin });
      }
      const resp = await axios.patch(`${API_BASE}/users/me`, { pin: newPin });
      updateUser(resp.data);
      setShowPinChange(false); setCurrentPin(""); setNewPin("");
      setSuccessMsg(user.has_pin ? "PIN changed!" : "PIN set!");
      setTimeout(() => setSuccessMsg(""), 2500);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setPinError(typeof detail === "string" ? detail : "PIN change failed");
    } finally { setPinSaving(false); }
  };

  const handleLinkGoogle = () => {
    setGoogleError("");
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      setGoogleError("Google login not available");
      return;
    }

    const initAndPrompt = () => {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          try {
            const resp = await axios.post(`${API_BASE}/users/me/link-google`, {
              id_token: response.credential,
            });
            updateUser(resp.data);
            setSuccessMsg("Google account linked!");
            setTimeout(() => setSuccessMsg(""), 2500);
          } catch (e) {
            setGoogleError(e.response?.data?.detail || "Failed to link Google account");
          }
        },
      });
      window.google.accounts.id.prompt();
    };

    if (window.google?.accounts?.id) {
      initAndPrompt();
    } else {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.onload = () => {
        if (window.google?.accounts?.id) initAndPrompt();
        else setGoogleError("Google login not available");
      };
      script.onerror = () => setGoogleError("Failed to load Google login");
      document.head.appendChild(script);
    }
  };

  const handleUnlinkGoogle = async () => {
    setGoogleError("");
    if (!user.has_pin) {
      setGoogleError(t("profile.set_pin_first"));
      return;
    }
    if (!window.confirm(t("profile.unlink_confirm"))) return;
    try {
      const resp = await axios.post(`${API_BASE}/users/me/unlink-google`);
      updateUser(resp.data);
      setSuccessMsg("Google account unlinked!");
      setTimeout(() => setSuccessMsg(""), 2500);
    } catch (e) {
      setGoogleError(e.response?.data?.detail || "Failed to unlink Google account");
    }
  };

  return (
    <div style={{
      flex: 1, minHeight: "100%",
      background: C.bg,
      paddingBottom: mobile ? 80 : 48,
    }}>
      <div style={{
        margin: "0 auto",
        padding: mobile ? "28px 16px" : "36px 18px",
      }}>

        {/* -- Profile card ----------------------------------------- */}
        <Card>
          {/* Avatar + name */}
          <div style={{ marginBottom: 6 }}>
            <input ref={photoInputRef} type="file" accept="image/*" style={{ display: "none" }}
              onChange={handlePhotoUpload} />
            <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 8 }}>
              <div
                onClick={() => photoInputRef.current?.click()}
                style={{
                  width: 38, height: 38, borderRadius: "50%",
                  background: user.profile_photo_url
                    ? `url(${user.profile_photo_url}) center/cover`
                    : `linear-gradient(135deg, ${C.primaryDim}, ${C.primary})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: FH, fontStyle: "italic",
                  fontSize: 16, color: "#fff6ef", fontWeight: 700, flexShrink: 0,
                  cursor: "pointer", position: "relative",
                  opacity: uploadingPhoto ? 0.5 : 1, transition: "opacity 0.2s",
                }}
              >
                {!user.profile_photo_url && user.nickname?.[0]?.toUpperCase()}
                <div style={{
                  position: "absolute", bottom: -2, right: -2,
                  width: 14, height: 14, borderRadius: "50%",
                  background: C.primaryContainer, border: `1.5px solid ${C.surfaceLowest}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 8, color: C.primary }}>edit</span>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <h2 style={{ margin: 0, fontFamily: FH, fontSize: 24, fontWeight: 700, color: C.onSurface }}>
                  {user.nickname}
                </h2>
                {!editing && (
                  <button
                    onClick={() => {
                      setNickname(user.nickname);
                      setInstagramUrl(user.instagram_url || "");
                      setNickStatus(null);
                      setEditing(true);
                    }}
                    style={{
                      border: "none", background: "none", cursor: "pointer", padding: 3,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: C.outlineVariant, borderRadius: 6,
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 14 }}>edit</span>
                  </button>
                )}
              </div>
            </div>
            <div>
              <div style={{ display: "flex", gap: 3, marginTop: 3, position: "relative" }}>
                {user.instagram_url && (
                  <a href={user.instagram_url} target="_blank" rel="noreferrer"
                    style={{ flex: 1, fontFamily: FL, fontSize: 14, color: "#E1306C", textDecoration: "none", fontWeight: 600, padding: "8px 0", background: "#fce4ec", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 3 }}>
                    Instagram
                  </a>
                )}
                {isPublic && (
                  <div data-tutorial="profile-share" style={{ flex: 1, position: "relative" }}>
                    <button
                      onClick={() => setShowShareMenu(!showShareMenu)}
                      style={{
                        width: "100%", background: C.surfaceLow, border: "none", padding: "8px 0", borderRadius: 6, cursor: "pointer",
                        fontFamily: FL, fontSize: 14, fontWeight: 600, color: C.primary,
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
                      }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 13 }}>share</span>
                      Share
                    </button>
                    {showShareMenu && (
                      <>
                        <div onClick={() => setShowShareMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 49 }} />
                        <div style={{
                          position: "absolute", top: "100%", right: 0, marginTop: 4, width: 220, zIndex: 50,
                          background: C.surfaceLowest, borderRadius: 12, overflow: "hidden",
                          boxShadow: "0 4px 20px rgba(47,52,48,0.12)",
                        }}>
                          <button
                            onClick={() => {
                              const url = `${FRONTEND_URL}/@${encodeURIComponent(user.nickname)}`;
                              navigator.clipboard.writeText(url).then(() => {
                                setSuccessMsg("Profile link copied!");
                                setTimeout(() => setSuccessMsg(""), 2500);
                              });
                              setShowShareMenu(false);
                            }}
                            style={{
                              width: "100%", padding: "13px 16px", border: "none", background: "none",
                              display: "flex", alignItems: "center", gap: 12, cursor: "pointer",
                              fontFamily: FL, fontSize: 16, fontWeight: 600, color: C.onSurface,
                              borderBottom: `1px solid ${C.container}`,
                            }}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: 18, color: C.primary }}>link</span>
                            Profile Link
                          </button>
                          <button
                            onClick={async () => {
                              setShowShareMenu(false);
                              try {
                                const profileUrl = `${FRONTEND_URL}/@${encodeURIComponent(user.nickname)}`;
                                await shareProfileCard(
                                  { nickname: user.nickname, profile_photo_url: user.profile_photo_url },
                                  personalPlaces,
                                  profileUrl,
                                );
                              } catch {
                                setSuccessMsg("Failed to generate share card");
                                setTimeout(() => setSuccessMsg(""), 2500);
                              }
                            }}
                            style={{
                              width: "100%", padding: "13px 16px", border: "none", background: "none",
                              display: "flex", alignItems: "center", gap: 12, cursor: "pointer",
                              fontFamily: FL, fontSize: 16, fontWeight: 600, color: C.onSurface,
                            }}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: 18, color: C.primary }}>badge</span>
                            Profile Card
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Public/Private toggle */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: C.surfaceLow,
            borderRadius: 6, padding: "10px 12px",
            marginBottom: 6,
          }}>
            <div>
              <p style={{ margin: 0, fontFamily: FL, fontSize: 14, fontWeight: 600, color: C.onSurface }}>
                Public Profile
              </p>
              <p style={{ margin: "2px 0 0", fontFamily: FL, fontSize: 12, color: C.outlineVariant, fontStyle: "italic" }}>
                {isPublic ? "Anyone can follow you" : "Visible by request only"}
              </p>
            </div>
            <Toggle value={isPublic} onChange={handlePublicToggle} />
          </div>

          {/* Push notification toggle */}
          {pushSupported && (
            <>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: C.surfaceLow, borderRadius: 6, padding: "10px 12px",
                marginBottom: pushError ? 4 : 14, opacity: pushLoading ? 0.6 : 1,
              }}>
                <div>
                  <p style={{ margin: 0, fontFamily: FL, fontSize: 14, fontWeight: 600, color: C.onSurface }}>
                    Notifications
                  </p>
                  <p style={{ margin: "2px 0 0", fontFamily: FL, fontSize: 12, color: C.outlineVariant, fontStyle: "italic" }}>
                    {pushEnabled ? "Likes, comments, and follow alerts" : "Turn on to receive notifications"}
                  </p>
                </div>
                <Toggle value={pushEnabled} onChange={handlePushToggle} />
              </div>
              {pushError && (
                <p style={{ margin: "0 0 12px", fontFamily: FL, fontSize: 14, color: C.error, padding: "0 14px" }}>
                  {pushError}
                </p>
              )}
            </>
          )}

          {/* Edit Profile form */}
          {editing && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <ProfileInput
                label={t("profile.nickname")}
                value={nickname}
                onChange={(e) => { setNickname(e.target.value); checkNickname(e.target.value); }}
              />
              {nickname.trim() !== user.nickname && nickname.trim().length >= 2 && nickStatus && (
                <p style={{
                  margin: "-6px 0 0 2px", fontFamily: FL, fontSize: 11, fontWeight: 600,
                  color: nickStatus === "available" ? "#1D9E75" : nickStatus === "taken" ? C.error : C.outlineVariant,
                }}>
                  {nickStatus === "checking" ? t("auth.checking") : nickStatus === "available" ? t("auth.available") : t("auth.taken")}
                </p>
              )}
              <ProfileInput
                label={t("profile.instagram")}
                value={instagramUrl}
                onChange={(e) => setInstagramUrl(e.target.value)}
                placeholder={t("profile.instagram_placeholder")}
              />
              {error && (
                <p style={{ fontFamily: FL, fontSize: 16, color: C.error, margin: 0 }}>{error}</p>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => { setEditing(false); setError(""); setNickStatus(null); }}
                  style={{
                    flex: 1, padding: "11px",
                    border: "1px solid rgba(101,93,84,0.12)",
                    borderRadius: 8, background: "none",
                    fontFamily: FL, fontSize: 14, color: C.onSurfaceVariant, cursor: "pointer",
                  }}
                >
                  {t("profile.cancel")}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    flex: 1, padding: "11px", border: "none", borderRadius: 8,
                    background: saving ? C.container : `linear-gradient(to bottom, ${C.primary}, ${C.primaryDim})`,
                    color: saving ? C.outlineVariant : "#fff6ef",
                    fontFamily: FL, fontSize: 14, fontWeight: 700,
                    cursor: saving ? "not-allowed" : "pointer",
                    transition: "opacity 0.15s",
                  }}
                >
                  {saving ? "Saving..." : t("profile.save")}
                </button>
              </div>
            </div>
          )}

          {successMsg && (
            <p style={{ fontFamily: FL, fontSize: 16, color: "#1D9E75", textAlign: "center", marginTop: 12 }}>
              {successMsg}
            </p>
          )}
        </Card>

        {/* -- Settings (collapsible) ------------------------------- */}
        <Card>
          <SectionHeader title={t("profile.settings")} open={openSections.pin} onToggle={() => toggleSection("pin")} />
          {openSections.pin && (
            <>
              {/* PIN Section */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <p style={{ margin: 0, fontFamily: FL, fontSize: 16, fontWeight: 600, color: C.onSurface }}>
                    {user.has_pin ? t("profile.change_pin") : t("profile.set_pin")}
                  </p>
                  <p style={{ margin: "2px 0 0", fontFamily: FL, fontSize: 14, color: C.outlineVariant }}>
                    6-digit PIN
                  </p>
                </div>
                <button
                  onClick={() => { setShowPinChange(!showPinChange); setPinError(""); }}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid rgba(101,93,84,0.12)",
                    borderRadius: 7, background: "none",
                    fontFamily: FL, fontSize: 14, color: C.onSurfaceVariant, cursor: "pointer",
                  }}
                >
                  {showPinChange ? "Cancel" : user.has_pin ? "Change" : "Set"}
                </button>
              </div>

              {showPinChange && (
                <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
                  {user.has_pin && (
                    <ProfileInput
                      label={t("profile.current_pin")} type="password" inputMode="numeric" maxLength={6}
                      value={currentPin}
                      onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder={t("profile.pin_placeholder")} style={{ textAlign: "center", letterSpacing: 10, fontSize: 20 }}
                    />
                  )}
                  <ProfileInput
                    label={t("profile.new_pin")} type="password" inputMode="numeric" maxLength={6}
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder={t("profile.pin_placeholder")} style={{ textAlign: "center", letterSpacing: 10, fontSize: 20 }}
                  />
                  {pinError && (
                    <p style={{ fontFamily: FL, fontSize: 16, color: C.error, margin: 0 }}>{pinError}</p>
                  )}
                  <button
                    onClick={handlePinChange}
                    disabled={pinSaving}
                    style={{
                      width: "100%", padding: "11px", border: "none", borderRadius: 8,
                      background: pinSaving
                        ? C.container
                        : `linear-gradient(to bottom, ${C.primary}, ${C.primaryDim})`,
                      color: pinSaving ? C.outlineVariant : "#fff6ef",
                      fontFamily: FL, fontSize: 14, fontWeight: 700,
                      cursor: pinSaving ? "not-allowed" : "pointer",
                    }}
                  >
                    {pinSaving ? "Saving..." : user.has_pin ? t("profile.change_pin") : t("profile.set_pin")}
                  </button>
                </div>
              )}

              {/* Google Account Link */}
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid rgba(101,93,84,0.08)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <p style={{ margin: 0, fontFamily: FL, fontSize: 16, fontWeight: 600, color: C.onSurface }}>
                      Google
                    </p>
                    <p style={{ margin: "2px 0 0", fontFamily: FL, fontSize: 14, color: C.outlineVariant }}>
                      {user.has_google ? t("profile.google_linked") : t("profile.google_not_linked")}
                    </p>
                  </div>
                  {user.has_google ? (
                    <button
                      onClick={handleUnlinkGoogle}
                      style={{
                        padding: "6px 12px",
                        border: "1px solid rgba(158,66,44,0.2)",
                        borderRadius: 7, background: "none",
                        fontFamily: FL, fontSize: 14, color: C.error, cursor: "pointer",
                      }}
                    >
                      {t("profile.unlink")}
                    </button>
                  ) : (
                    <button
                      onClick={handleLinkGoogle}
                      style={{
                        padding: "6px 12px",
                        border: "1px solid rgba(101,93,84,0.12)",
                        borderRadius: 7, background: "none",
                        fontFamily: FL, fontSize: 14, color: C.onSurfaceVariant, cursor: "pointer",
                      }}
                    >
                      {t("profile.link")}
                    </button>
                  )}
                </div>
                {googleError && (
                  <p style={{ fontFamily: FL, fontSize: 13, color: C.error, margin: "8px 0 0" }}>{googleError}</p>
                )}
              </div>

              {/* Language */}
              <div style={{ marginTop: 16 }}>
                <p style={{ fontFamily: FL, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.15em", color: C.outlineVariant, marginBottom: 8 }}>
                  Language
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      style={{
                        padding: "6px 12px", borderRadius: 8,
                        border: i18n.language === lang.code ? `1.5px solid ${C.primary}` : "1.5px solid transparent",
                        background: i18n.language === lang.code ? C.primaryContainer : C.surfaceLow,
                        fontFamily: FL, fontSize: 12, fontWeight: i18n.language === lang.code ? 700 : 500,
                        color: i18n.language === lang.code ? C.primary : C.onSurfaceVariant,
                        cursor: "pointer",
                      }}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>

            </>
          )}
        </Card>

        {/* -- Tutorial --------------------------------- */}
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          {onStartTutorial && (
            <button
              onClick={onStartTutorial}
              style={{
                flex: 1, padding: "13px",
                border: `1px solid ${C.container}`,
                borderRadius: 10, background: "none",
                color: C.primary,
                fontFamily: FL, fontSize: 14, fontWeight: 700,
                cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = C.surfaceLow}
              onMouseLeave={(e) => e.currentTarget.style.background = "none"}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>school</span>
              {t("tutorial.start_tutorial")}
            </button>
          )}
        </div>

        {/* -- Sign Out --------------------------------------------- */}
        <button
          onClick={() => { if (window.confirm("Sign out?")) logout(); }}
          style={{
            width: "100%", padding: "13px",
            border: "1px solid rgba(158,66,44,0.2)",
            borderRadius: 10, background: "none",
            color: C.error,
            fontFamily: FL, fontSize: 14, fontWeight: 700,
            cursor: "pointer",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = "rgba(158,66,44,0.05)"}
          onMouseLeave={(e) => e.currentTarget.style.background = "none"}
        >
          Sign Out
        </button>

        <button
          onClick={() => {
            if (window.confirm("Are you sure you want to delete your account? All data will be permanently removed and cannot be recovered.")) {
              axios.delete(`${API_BASE}/users/me/account`)
                .then(() => { alert(t("profile.delete_confirm")); logout(); })
                .catch(() => alert(t("profile.delete_error")));
            }
          }}
          style={{
            width: "100%", padding: "13px", marginTop: 8,
            border: "none", borderRadius: 10,
            background: "none",
            color: C.outlineVariant,
            fontFamily: FL, fontSize: 14, fontWeight: 500,
            cursor: "pointer",
            transition: "color 0.15s",
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = C.error}
          onMouseLeave={(e) => e.currentTarget.style.color = C.outlineVariant}
        >
          {t("profile.delete_account")}
        </button>

        <p style={{
          fontFamily: FL, fontSize: 12, color: C.outlineVariant,
          textAlign: "center", marginTop: 20, letterSpacing: "0.12em",
        }}>
          v2.0
        </p>
      </div>
    </div>
  );
}
